import { client } from "./client";
import { signal } from "./signal";
import { fa, faNum } from "../game/cards";

/**
 * گردونه‌ی شانس — همان قراردادِ `Backend/cmd/bazivand/wheel_api.go` (docs/lucky-wheel.md).
 *
 * هر عدد از سرور می‌آید: خانه‌ها، احتمال‌ها، چرخشِ مانده، زنجیره، نیمه‌شبِ تهران. نتیجه را
 * هم سرور می‌گیرد؛ این‌جا فقط چرخ تا خانه‌ای که سرور گفته چرخانده می‌شود. کیف از پیامِ
 * wallet ِ سوکت عوض می‌شود و نه از این پاسخ — همان قاعده‌ی [[Client.wallet]].
 */

export type WheelSlice = { id: string; kind: string; amount: number; label: string; odds: number };

export type WheelSpinRow = {
  n: number; slice: number; slice_id: string; kind: string; amount: number;
  item: string; item_name: string; bonus: boolean; fallback: boolean; at_ms: number;
};

export type WheelState = {
  enabled: boolean;
  slices: WheelSlice[];
  left: number; allowed: number; used: number;
  locked: boolean; subscriber: boolean; sub_spins: number;
  streak: number; spun_today: boolean; streak_every: number; streak_mult: number; bonus_next: boolean;
  day: number; next_at_ms: number; now_ms: number;
  today: WheelSpinRow[];
};

export type SpinResult = {
  spin: WheelSpinRow; replay: boolean; pending: boolean;
  coins: number; gems: number; sub_until_ms: number;
  wheel: WheelState;
};

/** آخرین وضعیت — دکمه‌ی سربرگ و پنجره‌ی گردونه یکی را می‌بینند. null = هنوز نپرسیده‌ایم. */
export const wheel = signal<WheelState | null>(null);

/** اختلافِ ساعتِ سرور با این مرورگر (ms)؛ شمارش با همین ساخته می‌شود. */
let skew = 0;

/** وضعیتِ تازه از سرور. خطا = null و همان قبلی می‌مانَد — کارت با شبکه‌ی کند غیب نمی‌شود. */
export async function loadWheel(): Promise<WheelState | null> {
  try {
    const s = await client.rest<WheelState>("GET", "/v1/wheel");
    if (s) adopt(s);
    return s;
  } catch {
    return null;
  }
}

function adopt(s: WheelState): void {
  skew = s.now_ms - Date.now();
  wheel.value = s;
}

/**
 * یک چرخش. nonce کلیدِ یکتاییِ همین تلاش است: تکرارِ شبکه همان نتیجه را می‌گیرد و نه
 * چرخشِ دوم، پس دکمه با یک nonce تا پاسخ قفل می‌ماند. خطاها همان کدهای سرور‌اند:
 * account_required · no_spins · wheel_off · bad_nonce · too_many · store · network.
 */
export async function spinWheel(nonce: string): Promise<{ result?: SpinResult; error?: string }> {
  try {
    const r = await client.rest<SpinResult>("POST", "/v1/wheel/spin", { nonce });
    if (r?.wheel) adopt(r.wheel);
    return { result: r };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "network" };
  }
}

/** کلیدِ یکتای تلاش: ۲۴ نویسه از حروف و رقمِ لاتین — همان که سرور می‌پذیرد (۸ تا ۶۴). */
export function newNonce(): string {
  const b = new Uint8Array(18);
  crypto.getRandomValues(b);
  return "w" + Array.from(b, (x) => (x % 36).toString(36)).join("").slice(0, 23);
}

/** میلی‌ثانیه تا نیمه‌شبِ تهران، با ساعتِ سرور. */
export function msLeft(s: WheelState, now = Date.now()): number {
  return Math.max(0, s.next_at_ms - (now + skew));
}

// ─────────────────────────── متن ───────────────────────────

/** متنِ روی خانه. برچسبِ پیکربندی همیشه می‌چربد. */
export function sliceLabel(s: Pick<WheelSlice, "kind" | "amount" | "label">): string {
  if (s.label) return s.label;
  switch (s.kind) {
    case "coins": return `${faNum(s.amount)} سکه`;
    case "gems": return `${faNum(s.amount)} گوهر`;
    case "back": return "پشت کارت";
    case "carpet": return "فرش";
    case "sub_days": return `${fa(s.amount)} روز اشتراک`;
    default: return s.kind;
  }
}

/** آن‌چه بُرده شد، به فارسی — برای جمله‌ی «بردی: …». */
export function prizeText(r: Pick<WheelSpinRow, "kind" | "amount" | "item_name" | "fallback">): string {
  if (r.fallback) return `${faNum(r.amount)} سکه`;
  switch (r.kind) {
    case "coins": return `${faNum(r.amount)} سکه`;
    case "gems": return `${faNum(r.amount)} گوهر`;
    case "back": return r.item_name ? `پشت کارت «${r.item_name}»` : "یک پشت کارت تازه";
    case "carpet": return r.item_name ? `فرش «${r.item_name}»` : "یک فرش تازه";
    case "sub_days": return `${fa(r.amount)} روز اشتراک ماهانه`;
    default: return r.kind;
  }
}

/** یک جمله‌ی توضیح زیرِ جایزه، اگر لازم است. */
export function prizeNote(res: Pick<SpinResult, "pending" | "replay"> & { spin: Pick<WheelSpinRow, "kind" | "fallback" | "bonus"> }): string {
  if (res.pending) return "جایزه‌ات ثبت شد و تا چند لحظه‌ی دیگر به کیفت می‌رسد.";
  if (res.spin.fallback) {
    return res.spin.kind === "carpet"
      ? "همه‌ی فرش‌ها را داشتی؛ به جایش سکه گرفتی."
      : "همه‌ی پشت‌کارت‌ها را داشتی؛ به جایش سکه گرفتی.";
  }
  if (res.spin.bonus) return "روز ویژه‌ی زنجیره بود: جایزه چند برابر شد.";
  if (res.replay) return "این همان چرخش قبلی است.";
  return "";
}

/** درصدِ احتمال: «۲۸٪»، «۲٫۸٪». */
export function oddsText(o: number): string {
  const v = Math.round(o * 10) / 10;
  const whole = Math.trunc(v), tenth = Math.round((v - whole) * 10);
  return (tenth === 0 ? fa(whole) : `${fa(whole)}٫${fa(tenth)}`) + "٪";
}

/** «۵ ساعت و ۱۲ دقیقه»، «۱۲ دقیقه»، «کمتر از یک دقیقه». */
export function countdownText(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "کمتر از یک دقیقه";
  const h = Math.floor(m / 60), r = m % 60;
  if (h === 0) return `${fa(r)} دقیقه`;
  return r === 0 ? `${fa(h)} ساعت` : `${fa(h)} ساعت و ${fa(r)} دقیقه`;
}

/** چند خالِ نوارِ زنجیره پُر است — از ۱ تا every؛ روزِ every‌اُم همه پُر. */
export function streakDots(streak: number, every: number): number {
  if (every <= 0 || streak <= 0) return 0;
  const k = streak % every;
  return k === 0 ? every : k;
}

// ─────────────────────────── چرخ ───────────────────────────

/**
 * چرخش تا کجا — زاویه‌ی **مطلق** (درجه، ساعت‌گرد) که خانه‌ی index را زیرِ نشانگرِ بالا
 * می‌نشانَد.
 *
 * خانه‌ی i از زاویه‌ی i·seg (از بالا، ساعت‌گرد) شروع می‌شود. چرخاندنِ چرخ به اندازه‌ی R
 * نقطه‌ی a را به a+R می‌برد، پس نشانگرِ بالا نقطه‌ی ‎−R را می‌بیند. `jitter` (۰ تا ۱)
 * جای ایستادن را درونِ **شصت درصدِ میانیِ** خانه می‌برد: چرخی که همیشه وسطِ خانه بایستد
 * ماشینی است، و چرخی که لبِ خانه بایستد «یک خانه مانده بود» می‌سازد — برندِ ما «منصفانه» است.
 */
export function landingAngle(index: number, count: number, current: number, turns = 5, jitter = 0.5): number {
  if (count <= 0) return current;
  const seg = 360 / count;
  const at = (index + 0.5) * seg + (Math.min(1, Math.max(0, jitter)) - 0.5) * 0.6 * seg;
  const base = Math.ceil(current / 360) * 360 + turns * 360;
  return base + ((360 - (at % 360)) % 360);
}

/** خانه‌ای که زاویه‌ی rot زیرِ نشانگر می‌گذارد — وارونه‌ی landingAngle، برای آزمون و برچسب. */
export function sliceAt(rot: number, count: number): number {
  if (count <= 0) return -1;
  const seg = 360 / count;
  const a = (((-rot) % 360) + 360) % 360;
  return Math.min(count - 1, Math.floor(a / seg));
}

/**
 * مثلِ landingAngle، ولی «از min به بعد»: کوچک‌ترین زاویه‌ی ≥ min که خانه‌ی index را زیرِ
 * نشانگر می‌نشانَد. چرخ پیش از رسیدنِ پاسخ راه می‌افتد و هر جا که باشد باید با همان سرعت ترمز
 * بگیرد؛ با این، فاصله‌ی ترمز همیشه در [min, min+360) است و زمانِ ترمز تقریباً ثابت.
 */
export function landingAfter(index: number, count: number, min: number, jitter = 0.5): number {
  if (count <= 0) return min;
  const seg = 360 / count;
  const at = (index + 0.5) * seg + (Math.min(1, Math.max(0, jitter)) - 0.5) * 0.6 * seg;
  const stop = (360 - (at % 360)) % 360;
  return min + (((stop - min) % 360) + 360) % 360;
}

// ─────────────────────────── حرکت ───────────────────────────
//
// چرخ با لمس راه می‌افتد، نه با پاسخِ سرور: کمی عقب می‌کشد (WINDUP)، شتاب می‌گیرد (ACCEL)
// و با سرعتِ VMAX می‌چرخد تا پاسخ برسد؛ بعد از همان سرعت نرم ترمز می‌گیرد تا خانه‌ی سرور.
// همه تابعِ خالصِ زمان‌اند (ms از لمس) و اپ همین را دارد (WheelSpinner).

export const WINDUP_MS = 180;
export const WINDUP_DEG = 9;
export const ACCEL_MS = 420;
/** درجه در میلی‌ثانیه — دو دور در ثانیه. */
export const VMAX = 0.72;
/** از این لحظه سرعت ثابت است و ترمز می‌تواند شروع شود. */
export const CRUISE_AT = WINDUP_MS + ACCEL_MS;
/** کمترین فاصله‌ی ترمز تا خانه: یک‌ونیم دور، تا ایستادن «دیده» شود. */
export const MIN_BRAKE = 540;

/** زاویه‌ی چرخِ آزاد (پیش از ترمز) در لحظه‌ی t. */
export function freeAngle(from: number, t: number): number {
  if (t <= 0) return from;
  if (t < WINDUP_MS) return from - WINDUP_DEG * Math.sin((Math.PI / 2) * (t / WINDUP_MS));
  const back = from - WINDUP_DEG, u = t - WINDUP_MS;
  if (u < ACCEL_MS) return back + (VMAX * u * u) / (2 * ACCEL_MS);
  return back + (VMAX * ACCEL_MS) / 2 + VMAX * (u - ACCEL_MS);
}

/** مدتِ ترمز از a تا b، طوری که سرعتِ آغازِ ترمز همان VMAX باشد (بی پرش). */
export function brakeMs(a: number, b: number): number {
  return (3 * Math.max(0, b - a)) / VMAX;
}

/** زاویه در لحظه‌ی t از آغازِ ترمز: کاهشِ نرمِ درجه‌ی سه، و در پایان دقیقاً b. */
export function brakeAngle(a: number, b: number, t: number, total: number): number {
  if (total <= 0 || t >= total) return b;
  const k = 1 - Math.max(0, t) / total;
  return a + (b - a) * (1 - k * k * k);
}

/**
 * خانه‌ی «جایزه‌ی بزرگ» که طلایی کشیده می‌شود: بزرگ‌ترین سکه، اگر یکتا باشد و دستِ‌کم چهار
 * برابرِ کوچک‌ترین سکه. ‎−1 = ندارد (مثلاً جدولی که همه‌ی سکه‌هایش نزدیکِ هم‌اند).
 */
export function jackpotIndex(slices: Pick<WheelSlice, "kind" | "amount">[]): number {
  let best = -1, max = 0, min = Infinity, ties = 0;
  slices.forEach((s, i) => {
    if (s.kind !== "coins" || s.amount <= 0) return;
    min = Math.min(min, s.amount);
    if (s.amount > max) { max = s.amount; best = i; ties = 1; } else if (s.amount === max) ties++;
  });
  return best >= 0 && ties === 1 && max >= 4 * min ? best : -1;
}

/** چند سکه روی خانه کشیده شود: ۱ زیرِ ۱۰۰، ۲ زیرِ ۵۰۰، ۳ از ۵۰۰ به بالا. */
export function coinTier(amount: number): 1 | 2 | 3 {
  return amount >= 500 ? 3 : amount >= 100 ? 2 : 1;
}
